import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch,
  getDoc,
  getCountFromServer,
  limit as fsLimit,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth, ensureFirebaseAuth } from '../firebase';
import {
  Account,
  RecoveryRecord,
  FollowUp,
  PTPRecord,
  FieldVisit,
  PhotoRecord,
  VoiceNoteRecord,
  DocumentRecord,
  User,
  CommissionRecord,
  AccountAllocation,
  ActivityLog,
  NotificationItem,
  Bank,
  Zone,
  Branch,
  RecoveryDepartment,
  WhatsAppOfferLog,
  StaffMessage,
  Enquiry,
} from '../types';

export interface FirestoreSpeedStats {
  isPrimaryActive: boolean;
  connected: boolean;
  lastLatencyMs: number;
  averageLatencyMs: number;
  writeCount: number;
  readCount: number;
  totalOps: number;
  lastOperationAt: string | null;

  /* ---------------- WHAT THE BILL IS ACTUALLY MADE OF ----------------
   *
   * `readCount` counts OPERATIONS -- one listener attaching is one. That
   * is no use for judging a bill, because Firestore charges per DOCUMENT:
   * a single listener that pulls down 1,500 accounts is 1,500 reads, not
   * one.
   *
   * These two count documents, and split them the way the invoice does.
   */

  /**
   * Documents delivered BY THE SERVER. This is what you are charged for.
   */
  billedDocumentReads: number;

  /**
   * Documents served from the browser's own cache. These cost nothing, and
   * every one of them is a read that would have been billed before the
   * cache was turned on. The two numbers side by side are the saving.
   */
  cachedDocumentReads: number;
}

// Clean undefined values before writing to Firestore
// MANDATORY SECURITY RULE: Passwords and salts must NEVER be written to Firestore
export function sanitizeForFirestore<T extends Record<string, any>>(obj: T): Record<string, any> {
  const clean: Record<string, any> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (key === 'password' || key === 'passwordHash' || key === 'passwordSalt') {
      continue;
    }
    // Never store a base64 image in Firestore. The file already lives in
    // Cloud Storage and `downloadUrl` points at it, so keeping the encoded
    // copy here stored every photo twice -- roughly 270 KB of text per
    // record, in the expensive database rather than cheap file storage,
    // re-downloaded in full every time a photo list opened.
    // Any field holding an inline `data:` payload is dropped, whatever it is
    // called. Naming only the three known fields left a hole: when a Storage
    // upload failed, the base64 image was carried in `downloadUrl` instead and
    // went into the document at full size.
    if (typeof val === 'string' && val.startsWith('data:') && val.length > 2048) {
      continue;
    }
    if (val !== undefined) {
      clean[key] = sanitizeValue(val);
    }
  }
  return clean;
}

/**
 * ARRAYS WERE NEVER CLEANED, AND THAT IS WHY SETTINGS WOULD NOT SAVE.
 *
 * The loop above skipped `undefined` at the top level and recursed into
 * plain objects, but an ARRAY was copied through untouched. An OTS slab is
 * an object inside an array, and a slab of the "on or before" kind has no
 * second date -- so it carried `date2: undefined` straight into Firestore,
 * which refuses the whole write:
 *
 *   "Unsupported field value: undefined (found in document settings/ots)"
 *
 * The entire settings document was therefore rejected every time. Every
 * symptom followed from this one line: slabs that reappeared after a
 * refresh, deadlines that would not change, and an OTS quoted from the
 * built-in defaults because the administrator's own scheme had never
 * reached the database.
 *
 * Every value is now cleaned by the same rules whether it sits in a field,
 * in a nested object, or in an array of either.
 */
function sanitizeValue(val: any): any {
  if (val === null) return null;

  if (Array.isArray(val)) {
    // An undefined ENTRY is dropped; Firestore will not store one, and a
    // hole in an array is not meaningful data.
    return val
      .filter((item) => item !== undefined)
      .map((item) => sanitizeValue(item));
  }

  if (typeof val === 'object' && !(val instanceof Date)) {
    return sanitizeForFirestore(val);
  }

  return val;
}

class FirebaseFirestoreService {
  private latencyHistory: number[] = [24, 28, 22];
  private stats: FirestoreSpeedStats = {
    isPrimaryActive: true,
    connected: false,
    lastLatencyMs: 24,
    averageLatencyMs: 25,
    writeCount: 0,
    readCount: 0,
    totalOps: 0,
    lastOperationAt: null,
    billedDocumentReads: 0,
    cachedDocumentReads: 0,
  };

  private listeners: ((stats: FirestoreSpeedStats) => void)[] = [];

  constructor() {
    if (typeof window !== 'undefined') {
      onAuthStateChanged(auth, (user) => {
        this.stats.connected = !!user;
        this.notify();
      });
    }
  }

  public subscribeStats(cb: (stats: FirestoreSpeedStats) => void): () => void {
    this.listeners.push(cb);
    cb(this.stats);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== cb);
    };
  }

  public getStats(): FirestoreSpeedStats {
    return { ...this.stats };
  }

  private notify() {
    this.listeners.forEach((cb) => {
      try {
        cb(this.getStats());
      } catch (e) {
        console.error('Firestore stat listener error:', e);
      }
    });
  }

  /**
   * Add a snapshot's documents to the right side of the ledger.
   *
   * `metadata.fromCache` says whether this snapshot was answered locally.
   * `docChanges()` is what makes the counting honest: on the first delivery
   * every document counts as a change, so the full cost shows -- but on
   * later deliveries only the documents that actually changed are counted,
   * which is exactly what Firestore charges for. Counting `snapshot.size`
   * instead would report 1,500 reads every time one account was edited.
   */
  private countSnapshot(snapshot: any) {
    try {
      const changed = snapshot?.docChanges?.().length ?? snapshot?.size ?? 0;
      if (snapshot?.metadata?.fromCache) {
        this.stats.cachedDocumentReads += changed;
      } else {
        this.stats.billedDocumentReads += changed;
      }
    } catch {
      /* counting must never break a listener */
    }
  }

  private recordLatency(start: number, isWrite: boolean = true) {
    const elapsed = Math.max(1, Math.round(performance.now() - start));
    this.latencyHistory.push(elapsed);
    if (this.latencyHistory.length > 20) {
      this.latencyHistory.shift();
    }
    const avg = Math.round(
      this.latencyHistory.reduce((a, b) => a + b, 0) / this.latencyHistory.length
    );

    this.stats.lastLatencyMs = elapsed;
    this.stats.averageLatencyMs = avg;
    if (isWrite) {
      this.stats.writeCount++;
    } else {
      this.stats.readCount++;
    }
    this.stats.totalOps++;
    this.stats.lastOperationAt = new Date().toLocaleTimeString();
    this.notify();
  }

  // ==========================================
  // REAL-TIME LISTENERS (Auth-Ready onSnapshot)
  // ==========================================

  /**
   * Helper that subscribes only when Firebase Auth is actively authenticated,
   * avoiding unauthenticated security rule violations.
   */
  /**
   * @param newestBy  Order by this field, newest first, and read only the most
   *                  recent `newestLimit` records. For a log that grows
   *                  without bound this is the difference between a sign-in
   *                  that reads three hundred records and one that reads every
   *                  record ever written. Only pass a field EVERY document in
   *                  the collection carries -- Firestore silently omits
   *                  documents that lack the field being ordered on.
   */
  private createAuthenticatedSubscription<T>(
    collectionName: string,
    callback: (items: T[]) => void,
    newestBy?: { field: string; limit: number }
  ): () => void {
    let unsubSnapshot: (() => void) | null = null;
    let isCancelled = false;

    const startListener = async () => {
  if (isCancelled || unsubSnapshot || !auth.currentUser) return;
  try {
    await auth.currentUser.getIdToken(false);
    const colRef = collection(db, collectionName);
    const source: any = newestBy
      ? query(colRef, orderBy(newestBy.field, 'desc'), fsLimit(newestBy.limit))
      : colRef;
    unsubSnapshot = onSnapshot(
          source,
          (snapshot) => {
            const items: T[] = [];
            snapshot.forEach((docSnap) => {
              // Keep the document ID. Previously only data() was used, so a
              // record that did not happen to store its own `id` field came
              // back with no identifier at all -- which made it impossible
              // to delete or update, because there was nothing to send.
              // A stored `id` still wins; this only fills in the gap.
              const raw = docSnap.data() as any;
              items.push({
                ...raw,
                // The record's own `id` field wins for display, but the real
                // Firestore document key is kept separately so deletes and
                // updates always target the right document.
                id: raw.id || docSnap.id,
                __docId: docSnap.id,
              } as T);
            });
            this.stats.connected = true;
            this.countSnapshot(snapshot);
            this.notify();
            callback(items);
          },
          (error) => {
            console.warn(`Firestore ${collectionName} listener notice:`, error.message);
          }
        );
      } catch (err: any) {
        console.warn(`Firestore ${collectionName} subscription notice:`, err?.message || err);
      }
    };

    if (auth.currentUser) {
      startListener();
    }

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        startListener();
      } else if (unsubSnapshot) {
        unsubSnapshot();
        unsubSnapshot = null;
        this.stats.connected = false;
        this.notify();
      }
    });

    return () => {
      isCancelled = true;
      if (unsubSnapshot) {
        unsubSnapshot();
        unsubSnapshot = null;
      }
      unsubAuth();
    };
  }

  public subscribeAccounts(callback: (accounts: Account[]) => void): () => void {
    return this.createAuthenticatedSubscription<Account>('accounts', callback);
  }

  /* ================================================================== *
   * SCOPED ACCOUNT READS
   *
   * `subscribeAccounts` above reads the ENTIRE accounts collection into
   * one live listener, for every signed-in user. At 400 accounts that is
   * free. At 80,000 it is 80,000 reads every time anybody signs in, and
   * Firestore bills reads.
   *
   * An agent has never needed more than their own allotment, and an
   * administrator has never needed the whole book to answer a question
   * about one branch. Everything below reads only what was asked for.
   * ================================================================== */

  /**
   * The accounts allotted to ONE agent, live.
   *
   * Two queries, merged, because an account is matched to an agent in two
   * different ways in the existing data:
   *
   *   1. `assignedAgentId` holding either the Agent ID (RA-0048) or the
   *      user's document id -- both are in use.
   *   2. `assignedAgentName` only, on older records where the allocation
   *      wrote the name and not the id.
   *
   * Dropping the second query would have silently hidden those older
   * accounts from the agent they belong to. Both are single-field
   * queries, so NO composite index has to be deployed -- if this needed
   * one, the first thing you would have seen is "the query requires an
   * index" and an empty screen.
   */
  public subscribeAccountsForAgent(
    idKeys: string[],
    agentNames: string[],
    callback: (accounts: Account[]) => void
  ): () => void {
    const keys = Array.from(new Set(idKeys.filter((k) => !!k))).slice(0, 30);
    const names = Array.from(new Set(agentNames.filter((n) => !!n))).slice(0, 10);

    if (keys.length === 0 && names.length === 0) {
      /**
       * No identity to scope by, so no question to ask.
       *
       * This used to answer with an empty list. That is not the same thing:
       * "nobody asked" was being reported as "you have no accounts", which
       * emptied the agent's screen whenever their profile was momentarily
       * between states. Saying nothing leaves whatever is on screen alone.
       */
      console.warn('[Accounts] No agent identity to scope by — no query sent.');
      return () => {};
    }

    let cancelled = false;
    const unsubs: Array<() => void> = [];
    // Each query keeps its own results, so one query's snapshot never
    // erases the other's.
    const buckets = new Map<string, Map<string, Account>>();

    /**
     * TWO PEOPLE CAN SHARE A NAME. THEY MUST NOT SHARE ACCOUNTS.
     *
     * An agency empanelled with two banks is created as two logins -- same
     * firm name, different staff codes -- because the banks, the branches and
     * the commission rates are different. But the second query below asks for
     * accounts by `assignedAgentName`, so BOTH logins matched every account
     * carrying that name: the Central Bank login was showing Mill Corner and
     * Garkheda, which are Bank of Maharashtra's.
     *
     * The name query cannot simply be removed. It is there for older accounts
     * that carry an agent's name and no staff code at all, and dropping it
     * would empty those agents' screens.
     *
     * So: an account found BY NAME is kept only if it has no staff code of
     * its own, or if that code is this person's. One that names somebody
     * else's code belongs to them, whatever the name says.
     */
    const ownKeys = new Set(keys.map((k) => String(k).trim().toLowerCase()));

    const emit = () => {
      if (cancelled) return;
      const merged = new Map<string, Account>();
      buckets.forEach((bucket, bucketId) => {
        const byName = bucketId.startsWith('byName');
        bucket.forEach((acc, key) => {
          if (byName) {
            const theirCode = String((acc as any).assignedAgentId || '').trim().toLowerCase();
            if (theirCode && !ownKeys.has(theirCode)) return;
          }
          merged.set(key, acc);
        });
      });
      callback(Array.from(merged.values()));
    };

    const attach = (bucketId: string, q: any) => {
      buckets.set(bucketId, new Map());
      const unsub = onSnapshot(
        q,
        (snapshot: any) => {
          this.countSnapshot(snapshot);
          const bucket = new Map<string, Account>();
          snapshot.forEach((docSnap: any) => {
            const raw = docSnap.data() as any;
            bucket.set(docSnap.id, {
              ...raw,
              id: raw.id || docSnap.id,
              __docId: docSnap.id,
            } as Account);
          });
          buckets.set(bucketId, bucket);
          this.stats.connected = true;
          this.notify();
          emit();
        },
        (error: any) => {
          console.warn(`Scoped accounts listener notice (${bucketId}):`, error?.message);
        }
      );
      unsubs.push(unsub);
    };

    const start = async () => {
      if (cancelled || unsubs.length > 0 || !auth.currentUser) return;
      try {
        await auth.currentUser.getIdToken(false);
        const colRef = collection(db, 'accounts');
        if (keys.length > 0) {
          attach('byId', query(colRef, where('assignedAgentId', 'in', keys)));
        }
        names.forEach((n, i) => {
          attach(`byName${i}`, query(colRef, where('assignedAgentName', '==', n)));
        });
      } catch (err: any) {
        console.warn('Scoped accounts subscription notice:', err?.message || err);
      }
    };

    if (auth.currentUser) void start();
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        void start();
      } else {
        unsubs.forEach((u) => u());
        unsubs.length = 0;
        buckets.clear();
      }
    });

    return () => {
      cancelled = true;
      unsubs.forEach((u) => u());
      unsubs.length = 0;
      unsubAuth();
    };
  }

  /**
   * How many accounts match, WITHOUT reading them.
   *
   * `getCountFromServer` is billed as one read per query no matter how many
   * documents it counts. This is what lets an administrator see "Agent
   * RA-0048: 1,240 accounts" for one read instead of one thousand two
   * hundred and forty.
   */
  public async countAccounts(scope?: {
    agentId?: string;
    branch?: string;
    bank?: string;
  }): Promise<number> {
    try {
      await ensureFirebaseAuth();
      const colRef = collection(db, 'accounts');
      const clauses: any[] = [];
      if (scope?.agentId) clauses.push(where('assignedAgentId', '==', scope.agentId));
      if (scope?.branch) clauses.push(where('branch', '==', scope.branch));
      if (scope?.bank) clauses.push(where('bank', '==', scope.bank));
      const q = clauses.length > 0 ? query(colRef, ...clauses) : colRef;
      const snap = await getCountFromServer(q as any);
      return snap.data().count;
    } catch (err: any) {
      console.warn('Account count notice:', err?.message || err);
      return -1;
    }
  }

  /**
   * One-off fetch of the accounts in a chosen scope, highest outstanding
   * first.
   *
   * Used by an administrator after choosing a bank, zone, branch or agent.
   * Nothing is read until that choice is made, which is the whole point.
   *
   * `orderBy` is applied in the browser rather than in the query. Combining
   * `where` with `orderBy` on a different field needs a composite index, and
   * a missing index fails the query outright instead of degrading -- an
   * empty screen and a console message most people never see. Sorting a
   * page of records here costs nothing.
   */
  public async fetchAccountsScoped(scope: {
    agentId?: string;
    agentName?: string;
    branch?: string;
    branches?: string[];
    /**
     * A single administrative zone.
     *
     * THE LEAK THIS CLOSES. A recovery department user is defined by a bank
     * AND a zone -- branch optional, and blank is supposed to mean "the whole
     * zone". But there was no way to ask for a zone here, so when no branches
     * were set the scope fell through to `bank` and the department loaded
     * EVERY account in the bank, in every zone, on every sign-in. Wrong data
     * in front of them, and the largest single read in the app.
     */
    zone?: string;
    bank?: string;
    max?: number;
  }): Promise<Account[]> {
    await ensureFirebaseAuth();
    const colRef = collection(db, 'accounts');

    const runs: any[] = [];
    if (scope.agentId) {
      runs.push(query(colRef, where('assignedAgentId', '==', scope.agentId)));
      if (scope.agentName) {
        runs.push(query(colRef, where('assignedAgentName', '==', scope.agentName)));
      }
    } else if (scope.branches && scope.branches.length > 0) {
      // `in` takes at most 30 values, so longer lists are split.
      for (let i = 0; i < scope.branches.length; i += 30) {
        runs.push(query(colRef, where('branch', 'in', scope.branches.slice(i, i + 30))));
      }
    } else if (scope.branch) {
      runs.push(query(colRef, where('branch', '==', scope.branch)));
    } else if (scope.zone) {
      runs.push(query(colRef, where('zone', '==', scope.zone)));
    } else if (scope.bank) {
      runs.push(query(colRef, where('bank', '==', scope.bank)));
    } else {
      // No scope at all. This is the expensive path and is only reached when
      // something explicitly asks for the whole book -- allocation and bulk
      // delete do, and they say so on screen before they run.
      runs.push(scope.max ? query(colRef, fsLimit(scope.max)) : colRef);
    }

    const merged = new Map<string, Account>();
    for (const q of runs) {
      const snap = await getDocs(q as any);
      this.countSnapshot(snap);
      /**
       * And tell the screen.
       *
       * The live listeners all notify after counting, so the header counter
       * follows them. This one-off fetch did not -- so the single most
       * visible read in the whole application, an administrator choosing a
       * bank and pulling down a thousand accounts, was counted internally and
       * never drawn. The counter appeared frozen at exactly the moment it had
       * most to report.
       */
      this.notify();
      snap.forEach((docSnap: any) => {
        const raw = docSnap.data() as any;
        merged.set(docSnap.id, {
          ...raw,
          id: raw.id || docSnap.id,
          __docId: docSnap.id,
        } as Account);
      });
    }

    let list = Array.from(merged.values());

    /**
     * TWO BANKS CAN HAVE A BRANCH OF THE SAME NAME.
     *
     * THE LEAK THIS CLOSES. Bank of Maharashtra has a Bidkin branch and
     * Central Bank of India has a Bidkin branch. The queries above ask for
     * accounts by branch NAME -- `branch == "Bidkin"` -- and nothing in them
     * mentions the bank. So a branch manager posted to one Bidkin loaded the
     * OTHER bank's Bidkin accounts as well: one bank's borrower data in front
     * of somebody empanelled for the other. The same applies to a zone name
     * shared between banks.
     *
     * WHY IT IS FILTERED HERE RATHER THAN IN THE QUERY. Firestore needs a
     * composite index to combine `branch in [...]` with `bank == "..."`, and
     * a missing composite index does not degrade -- the query fails outright
     * and the screen goes blank with a console message nobody reads. Dropping
     * the wrong bank's records after they arrive cannot fail that way. It
     * costs the same reads; it just refuses to show what is not theirs.
     *
     * AN ACCOUNT WITH NO BANK RECORDED IS KEPT. It cannot be attributed to
     * either bank, and making real accounts disappear from somebody's screen
     * is a worse fault than showing an unparented one. Those are visible in
     * the hierarchy screen as records to be given a bank.
     */
    /**
     * The same name-collision guard as the live listener.
     *
     * Asking for one agent runs two queries -- by staff code and by name --
     * because older accounts carry a name and no code. An agency empanelled
     * with two banks has two logins under ONE firm name, so the name query
     * returns both logins' accounts. An account naming somebody else's staff
     * code belongs to them.
     */
    if (scope.agentId && scope.agentName) {
      const own = String(scope.agentId).trim().toLowerCase();
      list = list.filter((a) => {
        const theirCode = String((a as any).assignedAgentId || '').trim().toLowerCase();
        return !theirCode || theirCode === own;
      });
    }

    const scopeBank = (scope.bank || '').trim().toLowerCase();
    const scopedByNameOnly =
      !scope.agentId && (scope.branch || (scope.branches && scope.branches.length > 0) || scope.zone);

    if (scopeBank && scopedByNameOnly) {
      list = list.filter((a) => {
        const theirs = String((a as any).bank || (a as any).bankName || '')
          .trim()
          .toLowerCase();
        return theirs === '' || theirs === scopeBank;
      });
    }

    list.sort(
      (a, b) =>
        (b.outstandingAmount || b.overdueAmount || 0) -
        (a.outstandingAmount || a.overdueAmount || 0)
    );
    return list;
  }

  /**
   * One collection, live, but only the records belonging to ONE agent.
   *
   * `recoveries`, `followups`, `ptps`, `visits`, `photos` and `voicenotes`
   * were each read in full, for every signed-in user. An agent with 40
   * accounts was reading every recovery, every PTP and every photo in the
   * business -- all of it discarded in the browser a moment later, all of it
   * billed.
   *
   * All six collections key off the same two fields, `agentId` and
   * `agentName`, so one function covers them.
   *
   * Two queries, merged, for the same reason as the accounts version: some
   * older records carry the name and no id. Both are single-field queries, so
   * NO composite index is needed -- a missing index fails a query outright
   * and would show an empty screen with a console message nobody reads.
   */
  public subscribeCollectionForAgent<T>(
    collectionName: string,
    idKeys: string[],
    agentNames: string[],
    callback: (items: T[]) => void
  ): () => void {
    const keys = Array.from(new Set(idKeys.filter((k) => !!k))).slice(0, 30);
    const names = Array.from(new Set(agentNames.filter((n) => !!n))).slice(0, 10);

    if (keys.length === 0 && names.length === 0) {
      // No identity to scope by. Returning nothing is safer than returning
      // everything, and it is visible rather than silently expensive.
      callback([] as T[]);
      return () => {};
    }

    let cancelled = false;
    const unsubs: Array<() => void> = [];
    // Each query keeps its own bucket, so one query's snapshot never erases
    // the other's results.
    const buckets = new Map<string, Map<string, T>>();

    /**
     * Same rule as the accounts listener above: a record found BY NAME is
     * kept only when it carries no staff code, or carries this person's. An
     * agency working with two banks has two logins under one firm name, and
     * one login must not show the other's receipts, visits or photos.
     */
    const ownKeys = new Set(keys.map((k) => String(k).trim().toLowerCase()));

    const emit = () => {
      if (cancelled) return;
      const merged = new Map<string, T>();
      buckets.forEach((bucket, bucketId) => {
        const byName = bucketId.startsWith('byName');
        bucket.forEach((item, key) => {
          if (byName) {
            const theirCode = String((item as any).agentId || '').trim().toLowerCase();
            if (theirCode && !ownKeys.has(theirCode)) return;
          }
          merged.set(key, item);
        });
      });
      callback(Array.from(merged.values()));
    };

    const attach = (bucketId: string, q: any) => {
      buckets.set(bucketId, new Map());
      const unsub = onSnapshot(
        q,
        (snapshot: any) => {
          this.countSnapshot(snapshot);
          const bucket = new Map<string, T>();
          snapshot.forEach((docSnap: any) => {
            const raw = docSnap.data() as any;
            bucket.set(docSnap.id, {
              ...raw,
              id: raw.id || docSnap.id,
              __docId: docSnap.id,
            } as T);
          });
          buckets.set(bucketId, bucket);
          this.stats.connected = true;
          this.notify();
          emit();
        },
        (error: any) => {
          console.warn(
            `Scoped ${collectionName} listener notice (${bucketId}):`,
            error?.message
          );
        }
      );
      unsubs.push(unsub);
    };

    const start = async () => {
      if (cancelled || unsubs.length > 0 || !auth.currentUser) return;
      try {
        await auth.currentUser.getIdToken(false);
        const colRef = collection(db, collectionName);
        if (keys.length > 0) {
          attach('byId', query(colRef, where('agentId', 'in', keys)));
        }
        names.forEach((n, i) => {
          attach(`byName${i}`, query(colRef, where('agentName', '==', n)));
        });
      } catch (err: any) {
        console.warn(
          `Scoped ${collectionName} subscription notice:`,
          err?.message || err
        );
      }
    };

    if (auth.currentUser) void start();
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        void start();
      } else {
        unsubs.forEach((u) => u());
        unsubs.length = 0;
        buckets.clear();
      }
    });

    return () => {
      cancelled = true;
      unsubAuth();
      unsubs.forEach((u) => u());
      unsubs.length = 0;
      buckets.clear();
    };
  }

  /**
   * How many records one agent has in one collection, without reading them.
   *
   * This is what lets an administrator see "RA-0048: 312 field visits" for a
   * single read instead of three hundred and twelve, and only load the
   * details for the agent actually chosen.
   */
  public async countCollectionForAgent(
    collectionName: string,
    agentId: string
  ): Promise<number> {
    try {
      await ensureFirebaseAuth();
      const q = query(collection(db, collectionName), where('agentId', '==', agentId));
      const snap = await getCountFromServer(q as any);
      return snap.data().count;
    } catch (err: any) {
      console.warn(`${collectionName} count notice:`, err?.message || err);
      return -1;
    }
  }

  public subscribeRecoveries(callback: (recoveries: RecoveryRecord[]) => void): () => void {
    return this.createAuthenticatedSubscription<RecoveryRecord>('recoveries', callback);
  }

  public subscribePTPs(callback: (ptps: PTPRecord[]) => void): () => void {
    return this.createAuthenticatedSubscription<PTPRecord>('ptps', callback);
  }

  public subscribeFollowUps(callback: (followups: FollowUp[]) => void): () => void {
    return this.createAuthenticatedSubscription<FollowUp>('followups', callback);
  }

  public subscribeVisits(callback: (visits: FieldVisit[]) => void): () => void {
    return this.createAuthenticatedSubscription<FieldVisit>('visits', callback);
  }

  public subscribePhotos(callback: (photos: PhotoRecord[]) => void): () => void {
    return this.createAuthenticatedSubscription<PhotoRecord>('photos', callback);
  }

  public subscribeVoiceNotes(callback: (voiceNotes: VoiceNoteRecord[]) => void): () => void {
    return this.createAuthenticatedSubscription<VoiceNoteRecord>('voicenotes', callback);
  }

  public subscribeDocuments(callback: (docs: DocumentRecord[]) => void): () => void {
    return this.createAuthenticatedSubscription<DocumentRecord>('documents', callback);
  }

  public subscribeUsers(callback: (users: User[]) => void): () => void {
    return this.createAuthenticatedSubscription<User>('users', callback);
  }

  public subscribeCommissions(callback: (commissions: CommissionRecord[]) => void): () => void {
    return this.createAuthenticatedSubscription<CommissionRecord>('commissions', callback);
  }

  /* ------------------------------------------------------------------ *
   * PUBLIC WEBSITE ENQUIRIES
   *
   * The "Join Us" form on scalesupport.co.in is filled in by a bank or an
   * agency who is NOT signed in, so this write deliberately does not go
   * through `ensureFirebaseAuth`. The security rules allow a create on this
   * one collection from anybody, in a fixed shape with capped lengths, and
   * nothing else -- no read, no update, no delete without an administrator.
   * ------------------------------------------------------------------ */

  public async saveEnquiry(enquiry: Enquiry): Promise<void> {
    await setDoc(doc(db, 'enquiries', enquiry.id), enquiry as any);
  }

  /** Every enquiry, newest first. Administrators only (rules enforce it). */
  public async fetchEnquiries(max = 500): Promise<Enquiry[]> {
    try {
      const snap = await getDocs(
        query(collection(db, 'enquiries'), orderBy('createdAt', 'desc'), fsLimit(max))
      );
      const out: Enquiry[] = [];
      snap.forEach((d) => {
        const raw = d.data() as any;
        out.push({ ...raw, id: raw.id || d.id } as Enquiry);
      });
      this.countSnapshot(snap as any);
      this.notify();
      return out;
    } catch (err: any) {
      console.warn('Enquiry fetch notice:', err?.message || err);
      return [];
    }
  }

  public async updateEnquiryStatus(id: string, status: Enquiry['status']): Promise<void> {
    await setDoc(doc(db, 'enquiries', id), { status }, { merge: true });
  }

  public async deleteEnquiry(id: string): Promise<void> {
    await deleteDoc(doc(db, 'enquiries', id));
  }

  public subscribeAllocations(callback: (allocations: AccountAllocation[]) => void): () => void {
    return this.createAuthenticatedSubscription<AccountAllocation>('allocations', callback);
  }

  /**
   * THE 412 READS THAT PAID FOR ONE NUMBER.
   *
   * `allocations` holds one record per account, so it is the same size as the
   * loan book -- four hundred and twelve records today. It was read IN FULL on
   * every administrator sign-in and every refresh, and it has exactly two
   * consumers anywhere in the app:
   *
   *   - a count on the Backend Storage screen ("412 Agent Allocations")
   *   - the "Account Allocated" entries on ONE customer's timeline
   *
   * Four hundred and twelve charged reads, on every load, to print one number
   * and to fill in a drawer almost nobody opens. The count is now asked for as
   * a count (one read), and the timeline asks for the allocations of the one
   * account being looked at (one or two reads, and only when the drawer is
   * opened).
   *
   * Nothing on any screen changes. The number is the same number.
   */
  public async countAllocations(): Promise<number> {
    try {
      await ensureFirebaseAuth();
      const snap = await getCountFromServer(collection(db, 'allocations'));
      return snap.data().count;
    } catch (err: any) {
      console.warn('Allocation count notice:', err?.message || err);
      return -1;
    }
  }

  /** The allocation history of ONE account, for its timeline drawer. */
  public async fetchAllocationsForAccount(accountId: string): Promise<AccountAllocation[]> {
    const id = String(accountId || '').trim();
    if (!id) return [];
    try {
      await ensureFirebaseAuth();
      const snap = await getDocs(
        query(collection(db, 'allocations'), where('accountId', '==', id))
      );
      const out: AccountAllocation[] = [];
      snap.forEach((d) => {
        const raw = d.data() as any;
        out.push({ ...raw, id: raw.id || d.id } as AccountAllocation);
      });
      this.countSnapshot(snap as any);
      this.notify();
      return out;
    } catch (err: any) {
      console.warn('Allocation fetch notice:', err?.message || err);
      return [];
    }
  }

  /**
   * These two grow for ever and were read IN FULL on every sign-in.
   *
   * One bulk allocation of four hundred accounts writes four hundred history
   * rows; do it three times in a morning and every sign-in that day reads
   * twelve hundred records before the first screen appears. Activity logs are
   * worse, because nothing ever removes one.
   *
   * Both are audit trails, read newest-first and never exhaustively, so the
   * most recent 300 is what a person actually looks at.
   */
  public subscribeAllocationHistories(callback: (histories: any[]) => void): () => void {
    return this.createAuthenticatedSubscription<any>('allocation_histories', callback, {
      field: 'date',
      limit: 300,
    });
  }

  /**
   * EVERY SIGN-IN, FETCHED ON ITS OWN.
   *
   * WHY THIS EXISTS. The agent sign-in panel counted `LOGIN` entries out of
   * the activity log, and that log is capped at the newest 300 (see below,
   * and for good reason). But EVERY action writes to the same log -- one
   * Excel import can add several hundred rows in a morning -- so the LOGIN
   * entries were pushed out past the 300 within a day and the panel showed
   * zero sign-ins for everybody. The counting was right; it was looking in a
   * bin that had already been filled with something else.
   *
   * Asking for LOGIN entries directly returns only sign-ins, so nothing can
   * crowd them out.
   *
   * ONE EQUALITY FILTER, DELIBERATELY. Adding a date range here (timestamp
   * >= the 1st of the month) would need a composite index, and a missing
   * composite index does not degrade -- the query fails outright and the
   * panel goes blank with a message only a developer would ever see. An
   * equality filter uses the automatic single-field index, so this cannot
   * fail that way. The month is then picked out in the browser, which costs
   * nothing.
   *
   * Fetched once when the panel opens, not subscribed: nobody needs a
   * sign-in count to update while they watch it.
   */
  public async fetchLoginLogs(max = 2000): Promise<ActivityLog[]> {
    try {
      await ensureFirebaseAuth();
      const q = query(
        collection(db, 'activityLogs'),
        where('actionType', '==', 'LOGIN'),
        fsLimit(max)
      );
      const snap = await getDocs(q);
      const out: ActivityLog[] = [];
      snap.forEach((d) => {
        const raw = d.data() as any;
        out.push({ ...raw, id: raw.id || d.id } as ActivityLog);
      });
      this.countSnapshot(snap as any);
      this.notify();
      return out;
    } catch (err: any) {
      console.warn('Login log fetch notice:', err?.message || err);
      return [];
    }
  }

  public subscribeActivityLogs(callback: (logs: ActivityLog[]) => void): () => void {
    return this.createAuthenticatedSubscription<ActivityLog>('activityLogs', callback, {
      field: 'timestamp',
      limit: 300,
    });
  }

  public subscribeNotifications(callback: (notifs: NotificationItem[]) => void): () => void {
    return this.createAuthenticatedSubscription<NotificationItem>('notifications', callback);
  }

  public subscribeBanks(callback: (banks: Bank[]) => void): () => void {
    return this.createAuthenticatedSubscription<Bank>('banks', callback);
  }

  public subscribeZones(callback: (zones: Zone[]) => void): () => void {
    return this.createAuthenticatedSubscription<Zone>('zones', callback);
  }

  public subscribeBranches(callback: (branches: Branch[]) => void): () => void {
    return this.createAuthenticatedSubscription<Branch>('branches', callback);
  }

  public subscribeDepartments(callback: (departments: RecoveryDepartment[]) => void): () => void {
    return this.createAuthenticatedSubscription<RecoveryDepartment>('departments', callback);
  }

  public subscribeWhatsAppLogs(callback: (logs: WhatsAppOfferLog[]) => void): () => void {
    return this.createAuthenticatedSubscription<WhatsAppOfferLog>('whatsapp_logs', callback);
  }

  /* ================================================================== *
   * STAFF MESSAGES
   *
   * An agent reads only the copies addressed to them, which is one small
   * query. Everybody else reads the recent traffic, capped, because a
   * message centre that grows for ever must not make signing in slower
   * every week -- the same mistake the activity log made.
   * ================================================================== */

  public subscribeMessagesForRecipient(
    recipientKeys: string[],
    callback: (messages: StaffMessage[]) => void
  ): () => void {
    const keys = Array.from(new Set(recipientKeys.filter(Boolean))).slice(0, 10);
    if (keys.length === 0) {
      console.warn('[Messages] No recipient identity to scope by — no query sent.');
      return () => {};
    }

    let cancelled = false;
    let unsub: (() => void) | null = null;

    const start = async () => {
      if (cancelled || unsub || !auth.currentUser) return;
      try {
        await auth.currentUser.getIdToken(false);
        const q = query(collection(db, 'messages'), where('recipientId', 'in', keys));
        unsub = onSnapshot(
          q,
          (snap: any) => {
            this.countSnapshot(snap);
            const rows: StaffMessage[] = [];
            snap.forEach((d: any) => {
              const raw = d.data() as any;
              rows.push({ ...raw, id: raw.id || d.id, __docId: d.id } as any);
            });
            this.stats.connected = true;
            this.notify();
            // Newest first, ordered here rather than in the query so a
            // record written before `sentAt` existed is never dropped.
            rows.sort((a, b) => (b.sentAt || '').localeCompare(a.sentAt || ''));
            callback(rows);
          },
          (err: any) => console.warn('[Messages] listener notice:', err?.message)
        );
      } catch (err: any) {
        console.warn('[Messages] subscription notice:', err?.message || err);
      }
    };

    if (auth.currentUser) void start();
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      if (u) void start();
      else {
        unsub?.();
        unsub = null;
      }
    });

    return () => {
      cancelled = true;
      unsub?.();
      unsubAuth();
    };
  }

  public subscribeAllMessages(callback: (messages: StaffMessage[]) => void): () => void {
    return this.createAuthenticatedSubscription<StaffMessage>('messages', callback, {
      field: 'sentAt',
      limit: 500,
    });
  }

  public async batchSaveMessages(rows: StaffMessage[]): Promise<void> {
    return this.batchSave('messages', rows, (m) => m.id);
  }

  public async deleteMessages(ids: string[]): Promise<void> {
    if (!auth.currentUser || ids.length === 0) return;
    const CHUNK = 400;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const batch = writeBatch(db);
      ids.slice(i, i + CHUNK).forEach((id) => batch.delete(doc(db, 'messages', id)));
      await batch.commit();
    }
  }

  // ==========================================
  // FAST MUTATIONS (< 50ms) - FIRESTORE OPTIMIZED
  // ==========================================

  public async saveAccount(account: Account): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = account.id || account.accountId;
      const cleanData = sanitizeForFirestore({
        ...account,
        updatedAt: new Date().toISOString(),
      });
      await setDoc(doc(db, 'accounts', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.error('[Firestore] account save FAILED:', e?.message || e);
      throw e;
    }
  }

  public async batchSaveAccounts(accounts: Account[]): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const CHUNK_SIZE = 400;
      for (let i = 0; i < accounts.length; i += CHUNK_SIZE) {
        const chunk = accounts.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);

        chunk.forEach((acc) => {
          const docId = acc.id || acc.accountId;
          const ref = doc(db, 'accounts', docId);
          batch.set(
            ref,
            sanitizeForFirestore({
              ...acc,
              updatedAt: new Date().toISOString(),
            }),
            { merge: true }
          );
        });

        await batch.commit();
      }
      this.recordLatency(start);
    } catch (e: any) {
      console.error('[Firestore] batch account save FAILED:', e?.message || e);
      throw e;
    }
  }

  /**
   * Deletes DO NOT swallow their errors.
   *
   * Swallowing made a refused or failed delete look exactly like a
   * successful one: the row vanished from the screen, the caller was told it
   * worked, and the live listener put it straight back on the next sync. The
   * photo delete had this same fault and it was just as confusing there.
   */
  /**
   * Deletes every document in a collection whose field matches a value.
   *
   * Deleting by document key alone kept failing: these records are saved
   * under `id || accountId`, the live listener reports its own key, and on
   * rows that came in through an import neither matches the key Firestore
   * actually used. Deleting a key that does not exist is silently treated as
   * success, so the record looked deleted and came back on the next sync.
   *
   * Matching on the FIELD finds the document whatever it is keyed by.
   */
  public async deleteWhere(
    collectionName: string,
    field: string,
    value: string
  ): Promise<number> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete records.');
    }
    if (!value) return 0;

    const snap = await getDocs(
      query(collection(db, collectionName), where(field, '==', value))
    );
    let removed = 0;
    for (const d of snap.docs) {
      await deleteDoc(doc(db, collectionName, d.id));
      removed += 1;
    }
    return removed;
  }

  /**
   * Reports what is actually in the accounts collection for an account
   * number: which document keys hold it, and which field the number is
   * stored under.
   *
   * Five attempts at deleting by key and by field have all reported success
   * and left the record in place, which means one of my assumptions about
   * how these documents are stored is wrong. Rather than guess a sixth time,
   * this asks the database directly and puts the answer on screen.
   */
  public async describeAccountDocs(
    accountId: string,
    candidateKeys: string[]
  ): Promise<{ byField: string[]; byKey: string[]; sampleFields: string[] }> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in.');
    }

    const byField: string[] = [];
    const sampleFields: string[] = [];
    try {
      const snap = await getDocs(
        query(collection(db, 'accounts'), where('accountId', '==', accountId))
      );
      snap.docs.forEach((d) => byField.push(d.id));
      if (snap.docs.length > 0) {
        sampleFields.push(...Object.keys(snap.docs[0].data()).slice(0, 25));
      }
    } catch (e: any) {
      byField.push(`QUERY FAILED: ${e?.code || e?.message || e}`);
    }

    const byKey: string[] = [];
    for (const k of candidateKeys) {
      try {
        const snap = await getDoc(doc(db, 'accounts', k));
        if (snap.exists()) byKey.push(k);
      } catch {
        /* ignore */
      }
    }

    return { byField, byKey, sampleFields };
  }

  /**
   * Deletes many documents in one round trip.
   *
   * Deleting one at a time cost about half a second each -- fine for one
   * account, unbearable for four hundred. Firestore takes up to 500 in a
   * single batch, so a whole agent's book now goes in one or two calls
   * instead of hundreds.
   *
   * Keys that do not exist are ignored, which is what lets us pass every
   * candidate key for every account without checking each one first.
   */
  public async batchDeleteDocs(
    collectionName: string,
    docIds: string[]
  ): Promise<number> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete records.');
    }
    const ids = Array.from(new Set(docIds.filter(Boolean)));
    if (ids.length === 0) return 0;

    const CHUNK = 450;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const batch = writeBatch(db);
      ids.slice(i, i + CHUNK).forEach((id) => {
        batch.delete(doc(db, collectionName, id));
      });
      await batch.commit();
    }
    return ids.length;
  }

  public async deleteAccount(accountId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete an account.');
    }
    await deleteDoc(doc(db, 'accounts', accountId));
  }

  public async saveRecovery(recovery: RecoveryRecord): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = recovery.id || recovery.recoveryId;
      const cleanData = sanitizeForFirestore({
        ...recovery,
        createdAt: (recovery as any).createdAt || (recovery as any).timestamp || new Date().toISOString(),
      });
      await setDoc(doc(db, 'recoveries', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.warn('Firestore recovery save notice:', e?.message || e);
    }
  }

  /**
   * Does this document still exist?
   *
   * WHY THIS IS NEEDED: Firestore treats "delete a document that is not
   * there" as SUCCESS. It is not an error and nothing is reported. So a
   * second delete of the same record returns exactly the same result as the
   * first one -- and any code that adjusts a balance "because the delete
   * succeeded" adjusts it again, and again, every time the button is pressed.
   *
   * Asking first is the only way to tell "I deleted it" from "it was already
   * gone". One read, and only on a delete.
   */
  public async docExists(collectionName: string, docId: string): Promise<boolean> {
    if (!docId) return false;
    try {
      await ensureFirebaseAuth();
      const snap = await getDoc(doc(db, collectionName, docId));
      return snap.exists();
    } catch (err: any) {
      console.warn(`${collectionName} existence check notice:`, err?.message || err);
      // Unknown is treated as "still there", so a balance is never adjusted
      // on a guess.
      return true;
    }
  }

  public async deleteRecovery(recoveryId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete a collection.');
    }
    await deleteDoc(doc(db, 'recoveries', recoveryId));
  }

  public async saveCommission(commission: CommissionRecord): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = commission.id;
      const cleanData = sanitizeForFirestore({
        ...commission,
        createdAt: (commission as any).createdAt || new Date().toISOString(),
      });
      await setDoc(doc(db, 'commissions', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.warn('Firestore commission save notice:', e?.message || e);
    }
  }

  public async deleteCommission(commissionId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete a commission entry.');
    }
    await deleteDoc(doc(db, 'commissions', commissionId));
  }

  public async savePTP(ptp: PTPRecord): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = ptp.id;
      const cleanData = sanitizeForFirestore({
        ...ptp,
        createdAt: ptp.createdAt || new Date().toISOString(),
      });
      await setDoc(doc(db, 'ptps', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.warn('Firestore PTP save notice:', e?.message || e);
    }
  }

  public async deletePTP(ptpId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'ptps', ptpId));
  }

  public async saveFollowUp(followup: FollowUp): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = followup.id;
      const cleanData = sanitizeForFirestore(followup);
      await setDoc(doc(db, 'followups', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.warn('Firestore followup save notice:', e?.message || e);
    }
  }

  public async deleteFollowUp(followupId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'followups', followupId));
  }

  public async saveVisit(visit: FieldVisit): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = visit.id || visit.visitId;
      const cleanData = sanitizeForFirestore(visit);
      await setDoc(doc(db, 'visits', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.warn('Firestore visit save notice:', e?.message || e);
    }
  }

  public async deleteVisit(visitId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'visits', visitId));
  }

  /**
   * THE FILE ITSELF NEVER GOES IN THE DATABASE. STRIPPED HERE SO IT CANNOT.
   *
   * WHAT WAS HAPPENING. A photo record carries `dataUrl` -- the entire
   * watermarked image encoded as base64 text, which is what puts it on the
   * screen the moment it is taken. That whole record was being written to
   * Firestore, so every photo was stored TWICE: once properly in Cloud
   * Storage, and once as a wall of text inside a database document.
   *
   * THREE THINGS THAT WERE WRONG WITH IT.
   *
   * First, and worst: a Firestore document cannot exceed 1 MB, and base64
   * makes an image about a third larger. A 750 KB photo becomes roughly a
   * megabyte of text and the write is REJECTED. The rejection was caught and
   * written to the console, where nobody sees it -- so the photo sat in Cloud
   * Storage while its record silently failed to save, and it never appeared
   * in anybody's list. A large photo simply vanished.
   *
   * Second, it is the expensive place to keep it: Firestore storage costs
   * about seven times what Cloud Storage costs for the same bytes.
   *
   * Third, it made every read heavier. An administrator's session reads the
   * photos collection, and that meant pulling every image's bytes down as
   * well as its details.
   *
   * `downloadUrl` and `storagePath` are what the record actually needs -- the
   * file's address in Cloud Storage. The bytes stay in the browser for the
   * session that took the photo and are re-fetched from that address
   * afterwards, which is how it should always have worked.
   *
   * The same stripping is applied to voice notes below, for the same reason.
   */
  private stripStoredFileBytes<T extends Record<string, any>>(record: T): T {
    const copy: any = { ...record };
    // The watermarked image as base64.
    delete copy.dataUrl;
    // A `blob:` URL is valid only inside the page that made it. Stored, it is
    // dead on arrival -- it can never resolve for anybody else, or for the
    // same person after a refresh.
    delete copy.audioBlobUrl;

    /**
     * AND THE BYTES DISGUISED AS AN ADDRESS.
     *
     * When a Cloud Storage upload fails, the upload helper falls back to
     * returning the base64 image AS the download URL, so the agent still sees
     * their photo rather than losing it. Good intent -- but it means the
     * whole image arrives here wearing a different field name, and writing it
     * would fail the document for exactly the reason `dataUrl` did.
     *
     * A `data:` URL is content, not a location. It is dropped, and the record
     * is marked so the failure is visible instead of silent: a photo whose
     * upload did not complete now SAYS so, rather than looking saved and
     * being unopenable by anybody else.
     */
    const looksInline = (v: any) => typeof v === 'string' && v.startsWith('data:');
    if (looksInline(copy.downloadUrl)) {
      delete copy.downloadUrl;
      copy.uploadStatus = 'Pending';
    }
    if (looksInline(copy.fileUrl)) {
      delete copy.fileUrl;
    }

    return copy as T;
  }

  public async savePhotoMetadata(photo: PhotoRecord): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = photo.id || photo.photoId;
      const cleanData = sanitizeForFirestore(this.stripStoredFileBytes(photo));
      await setDoc(doc(db, 'photos', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.warn('Firestore photo metadata save notice:', e?.message || e);
    }
  }

  /**
   * Writes the agent's note onto an existing photo record.
   *
   * Unlike savePhotoMetadata, errors here are NOT swallowed: a note that
   * silently failed to save would look edited on screen and then revert the
   * moment the live listener re-synced -- the same trap that made photo
   * deletes appear to work and then undo themselves.
   */
  public async updatePhotoNote(
    docId: string,
    fields: {
      agentRemark: string;
      noteEdited: boolean;
      noteEditedAt: string;
      noteEditedBy: string;
    }
  ): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to edit a photo note.');
    }
    await setDoc(doc(db, 'photos', docId), fields, { merge: true });
  }

  public async deletePhoto(photoId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete a photo.');
    }
    // Errors are deliberately NOT swallowed here. Hiding them made a failed
    // delete look successful: the photo vanished from the screen and then
    // reappeared as soon as the live listener re-synced.
    await deleteDoc(doc(db, 'photos', photoId));
  }

  public async saveVoiceNoteMetadata(voiceNote: VoiceNoteRecord): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = voiceNote.id || voiceNote.voiceNoteId;
      const cleanData = sanitizeForFirestore(this.stripStoredFileBytes(voiceNote));
      await setDoc(doc(db, 'voicenotes', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.warn('Firestore voicenote save notice:', e?.message || e);
    }
  }

  public async deleteVoiceNote(voiceNoteId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'voicenotes', voiceNoteId));
  }

  public async saveDocumentMetadata(docRecord: DocumentRecord): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = docRecord.id || docRecord.documentId;
      const cleanData = sanitizeForFirestore(docRecord);
      await setDoc(doc(db, 'documents', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.warn('Firestore document metadata save notice:', e?.message || e);
    }
  }

  public async deleteDocument(docId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'documents', docId));
  }

  public async saveUser(user: User, asAdmin = false): Promise<void> {
  if (!auth.currentUser) {
    throw new Error('No authenticated Firebase user');
  }

  // An administrator may save any profile; everyone else only their own.
  //
  // This used to refuse ANY save for another user and return quietly, on the
  // grounds that "a Cloud Function handles this" -- but nothing called one.
  // So an admin changing someone's name, role, bank list or commission rate
  // saw it change on screen, saved nothing, and lost it on the next reload.
  // The security rules already allow an admin to write any user document, so
  // the client was being stricter than the database.
  if (auth.currentUser.uid !== user.id && !asAdmin) {
    console.warn(
      '[Firestore] Not saving another user profile: caller is not an administrator.'
    );
    return;
  }

    const start = performance.now();

    try {
      const docId = user.id;
      const cleanData = sanitizeForFirestore(user);

      await setDoc(
        doc(db, 'users', docId),
        cleanData,
        { merge: true }
      );

      this.recordLatency(start);

      console.log('[Firestore] User profile saved successfully:', docId);
    } catch (e: any) {
      console.error('[Firestore] User profile save FAILED:', e);
      throw e;
    }
  }

  public async deleteUser(userId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'users', userId));
  }

  public async saveAllocation(allocation: AccountAllocation): Promise<void> {
    if (!auth.currentUser) return;
    const start = performance.now();
    try {
      const docId = allocation.id || `alloc_${Date.now()}`;
      const cleanData = sanitizeForFirestore(allocation);
      await setDoc(doc(db, 'allocations', docId), cleanData, { merge: true });
      this.recordLatency(start);
    } catch (e: any) {
      console.warn('Firestore allocation save notice:', e?.message || e);
    }
  }

  public async deleteAllocation(allocationId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'allocations', allocationId));
  }

  public async saveAllocationHistory(history: any): Promise<void> {
    if (!auth.currentUser) return;
    try {
      const docId = history.id || `alh_${Date.now()}`;
      const cleanData = sanitizeForFirestore(history);
      await setDoc(doc(db, 'allocation_histories', docId), cleanData, { merge: true });
    } catch (e: any) {
      console.warn('Firestore allocation history save notice:', e?.message || e);
    }
  }

  /**
   * Generic chunked batch writer. Firestore caps a batch at 500 operations,
   * so anything larger is split automatically.
   */
  private async batchSave(
    collectionName: string,
    rows: any[],
    idOf: (row: any) => string
  ): Promise<void> {
    if (!auth.currentUser || !rows || rows.length === 0) return;
    const start = performance.now();
    try {
      const CHUNK_SIZE = 400;
      for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
        const chunk = rows.slice(i, i + CHUNK_SIZE);
        const batch = writeBatch(db);
        chunk.forEach((row) => {
          const docId = idOf(row);
          if (!docId) return;
          batch.set(
            doc(db, collectionName, docId),
            sanitizeForFirestore(row),
            { merge: true }
          );
        });
        await batch.commit();
      }
      this.recordLatency(start);
    } catch (e: any) {
      console.error(`[Firestore] batch save to ${collectionName} FAILED:`, e?.message || e);
      throw e;
    }
  }

  public async batchSaveAllocations(allocations: AccountAllocation[]): Promise<void> {
    return this.batchSave('allocations', allocations, (a) => a.id || `alloc_${Date.now()}`);
  }

  public async batchSaveAllocationHistories(histories: any[]): Promise<void> {
    return this.batchSave('allocation_histories', histories, (h) => h.id || `alh_${Date.now()}`);
  }

  public async batchSaveBanks(banks: Bank[]): Promise<void> {
    return this.batchSave('banks', banks, (b) => b.id);
  }

  public async batchSaveZones(zones: Zone[]): Promise<void> {
    return this.batchSave('zones', zones, (z) => z.id);
  }

  public async batchSaveBranches(branches: Branch[]): Promise<void> {
    return this.batchSave('branches', branches, (b) => b.id);
  }

  public async batchSaveDepartments(depts: RecoveryDepartment[]): Promise<void> {
    return this.batchSave('departments', depts, (d) => d.id);
  }

  public async saveActivityLog(log: ActivityLog): Promise<void> {
    if (!auth.currentUser) return;
    try {
      const docId = log.id || `log_${Date.now()}`;
      const cleanData = sanitizeForFirestore(log);
      await setDoc(doc(db, 'activityLogs', docId), cleanData, { merge: true });
    } catch (e: any) {
      console.warn('Firestore activity log save notice:', e?.message || e);
    }
  }

  public async saveNotification(notif: NotificationItem): Promise<void> {
    if (!auth.currentUser) return;
    try {
      const docId = notif.id || `notif_${Date.now()}`;
      const cleanData = sanitizeForFirestore(notif);
      await setDoc(doc(db, 'notifications', docId), cleanData, { merge: true });
    } catch (e: any) {
      console.warn('Firestore notification save notice:', e?.message || e);
    }
  }

  public async saveBank(bank: Bank): Promise<void> {
    if (!auth.currentUser) return;
    try {
      const docId = bank.id;
      const cleanData = sanitizeForFirestore(bank);
      await setDoc(doc(db, 'banks', docId), cleanData, { merge: true });
    } catch (e: any) {
      console.warn('Firestore bank save notice:', e?.message || e);
    }
  }

  public async deleteBank(bankId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'banks', bankId));
  }

  public async saveZone(zone: Zone): Promise<void> {
    if (!auth.currentUser) return;
    try {
      const docId = zone.id;
      const cleanData = sanitizeForFirestore(zone);
      await setDoc(doc(db, 'zones', docId), cleanData, { merge: true });
    } catch (e: any) {
      console.warn('Firestore zone save notice:', e?.message || e);
    }
  }

  public async deleteZone(zoneId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'zones', zoneId));
  }

  public async saveBranch(branch: Branch): Promise<void> {
    if (!auth.currentUser) return;
    try {
      const docId = branch.id;
      const cleanData = sanitizeForFirestore(branch);
      await setDoc(doc(db, 'branches', docId), cleanData, { merge: true });
    } catch (e: any) {
      console.warn('Firestore branch save notice:', e?.message || e);
    }
  }

  public async deleteBranch(branchId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'branches', branchId));
  }

  public async saveDepartment(dept: RecoveryDepartment): Promise<void> {
    if (!auth.currentUser) return;
    try {
      const docId = dept.id;
      const cleanData = sanitizeForFirestore(dept);
      await setDoc(doc(db, 'departments', docId), cleanData, { merge: true });
    } catch (e: any) {
      console.warn('Firestore department save notice:', e?.message || e);
    }
  }

  public async deleteDepartment(deptId: string): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to delete this record.');
    }
    await deleteDoc(doc(db, 'departments', deptId));
  }

  public async saveWhatsAppLog(log: WhatsAppOfferLog): Promise<void> {
    if (!auth.currentUser) return;
    try {
      const docId = log.id || `wa_${Date.now()}`;
      const cleanData = sanitizeForFirestore(log);
      await setDoc(doc(db, 'whatsapp_logs', docId), cleanData, { merge: true });
    } catch (e: any) {
      console.warn('Firestore WhatsApp log save notice:', e?.message || e);
    }
  }

  /**
   * Errors are NOT swallowed. A refused write used to look exactly like a
   * successful one, so a commission rate could appear to save and then be
   * missing at the next sign-in with nothing to explain why.
   */
  /**
   * Settings REPLACE the document; they do not merge into it.
   *
   * THE BUG THIS FIXES: with `merge: true` a key that has been removed stays
   * in the database for ever, because merging only adds and overwrites. So
   * deleting an OTS slab or a bank deadline appeared to work, and the
   * deleted entry came straight back on the next load. Settings are one
   * whole object -- what the administrator has on screen IS the setting --
   * so the document is written in full.
   */
  public async saveSettings(settingId: string, data: Record<string, any>): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('You must be signed in to save settings.');
    }
    const cleanData = sanitizeForFirestore(data);
    await setDoc(doc(db, 'settings', settingId), cleanData);
  }

  /**
   * Reads a settings document back.
   *
   * saveSettings existed but nothing ever called it, and nothing read
   * settings either -- so the commission rates lived only in memory and went
   * every time anyone signed out.
   */
  public async loadSettings<T = Record<string, any>>(
    settingId: string
  ): Promise<T | null> {
    if (!auth.currentUser) return null;
    try {
      const snap = await getDoc(doc(db, 'settings', settingId));
      return snap.exists() ? (snap.data() as T) : null;
    } catch (e: any) {
      console.warn('Firestore settings load notice:', e?.message || e);
      return null;
    }
  }

  public async testConnection(): Promise<{ connected: boolean; latencyMs: number; error?: string }> {
    const start = performance.now();
    try {
      await ensureFirebaseAuth();
      // `test/connection` is the one document with a public read rule.
      // The old `system/connectivity_ping` path had no rule at all, so this
      // probe always reported "permission denied".
      const pingDocRef = doc(db, 'test', 'connection');
      await getDoc(pingDocRef);
      const elapsed = Math.max(1, Math.round(performance.now() - start));
      this.recordLatency(start, false);
      return { connected: true, latencyMs: elapsed };
    } catch (err: any) {
      return {
        connected: false,
        latencyMs: Math.round(performance.now() - start),
        error: err?.message || 'Firestore connection probe timed out',
      };
    }
  }

  /* ================================================================== *
   * THE FULL BACKUP
   *
   * WHY THIS EXISTS. The app already had an "Export Backend (.xlsx)"
   * button, and it looked like a backup, which made it dangerous. It
   * exported whatever happened to be IN THE BROWSER at that moment --
   * and since accounts are only loaded once a scope is picked, pressing
   * it on a fresh login could hand you a file with a fraction of the
   * database, or none of it, and no indication that anything was
   * missing. A backup you cannot trust is worse than no backup, because
   * you stop taking real ones.
   *
   * This reads every collection STRAIGHT FROM FIRESTORE. What comes back
   * is what is actually stored, not what a screen happens to be holding.
   *
   * IT IS NOT FREE. Firestore bills per document, and this reads all of
   * them. That is the whole point of it, and it is why nothing calls
   * this on a timer, on a page load, or as part of anything else. It
   * runs when a person presses the button and confirms the count.
   * ================================================================== */

  /**
   * Every collection the app writes to. `test` is left out -- it holds
   * one document used to time the connection and is not data.
   */
  public static readonly BACKUP_COLLECTIONS: string[] = [
    'accounts',
    'users',
    'recoveries',
    'ptps',
    'followups',
    'visits',
    'photos',
    'voicenotes',
    'documents',
    'commissions',
    'allocations',
    'allocation_histories',
    'activityLogs',
    'notifications',
    'messages',
    'banks',
    'zones',
    'branches',
    'departments',
    'whatsapp_logs',
    'settings',
  ];

  /**
   * Read the entire database, one collection at a time.
   *
   * Sequential on purpose. Firing twenty-one collection reads at once
   * gives a browser on a phone tether a good chance of dropping some of
   * them, and a backup that silently lost a collection is exactly the
   * failure this is meant to prevent.
   *
   * A collection that cannot be read is REPORTED, never skipped
   * quietly: `failed` comes back with the reason, and the caller is
   * expected to show it rather than present the file as complete.
   */
  public async fetchEntireDatabase(
    onProgress?: (info: { collection: string; index: number; total: number; count: number }) => void
  ): Promise<{
    data: Record<string, any[]>;
    counts: Record<string, number>;
    failed: Array<{ collection: string; reason: string }>;
    totalDocuments: number;
    takenAt: string;
  }> {
    await ensureFirebaseAuth();

    const names = FirebaseFirestoreService.BACKUP_COLLECTIONS;
    const data: Record<string, any[]> = {};
    const counts: Record<string, number> = {};
    const failed: Array<{ collection: string; reason: string }> = [];
    let totalDocuments = 0;

    for (let i = 0; i < names.length; i += 1) {
      const name = names[i];
      try {
        const snap = await getDocs(collection(db, name));
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
        data[name] = rows;
        counts[name] = rows.length;
        totalDocuments += rows.length;
        this.stats.readCount += rows.length;
        onProgress?.({ collection: name, index: i + 1, total: names.length, count: rows.length });
      } catch (err: any) {
        // A rule that forbids reading a collection lands here. The
        // backup continues, but it will say so.
        data[name] = [];
        counts[name] = 0;
        failed.push({ collection: name, reason: err?.message || 'could not be read' });
        onProgress?.({ collection: name, index: i + 1, total: names.length, count: 0 });
      }
    }

    return { data, counts, failed, totalDocuments, takenAt: new Date().toISOString() };
  }

  /**
   * How many documents a full backup would read, before running one.
   *
   * `getCountFromServer` is billed at one read per BATCH OF 1000, not one
   * per document, so asking this question first costs almost nothing --
   * and it means the person can be told "this will read about 4,300
   * documents" and decide, rather than finding out afterwards.
   */
  public async estimateBackupSize(): Promise<{ total: number; perCollection: Record<string, number> }> {
    await ensureFirebaseAuth();
    const perCollection: Record<string, number> = {};
    let total = 0;

    for (const name of FirebaseFirestoreService.BACKUP_COLLECTIONS) {
      try {
        const snap = await getCountFromServer(collection(db, name));
        const n = snap.data().count || 0;
        perCollection[name] = n;
        total += n;
      } catch {
        perCollection[name] = 0;
      }
    }

    return { total, perCollection };
  }
}

export const firebaseFirestoreService = new FirebaseFirestoreService();
